// ---------------------------------------------------------------------------
// freshness.ts — the ONE honest-state helper for every status indicator.
//
// Cross-cutting audit rule (ledger_UI_deadwire.md F-03/F-10/F-16):
//   No indicator may derive health from row EXISTENCE. A light may show its
//   healthy (green) color ONLY when the underlying data is FRESH. If the data
//   is stale, disconnected, or has no timestamp at all, the indicator must
//   render a neutral / slashed state that is visually unmistakable from green.
//
// Design note: when `lastSeen` is missing (many list endpoints do not yet
// return a per-entity data timestamp — see F-03 backend backlog), this returns
// 'unknown' → a NEUTRAL render, never green. That makes the helper honest
// immediately; adding backend timestamps later upgrades 'unknown' → precise
// fresh/stale/disconnected with no frontend change.
//
// Reference pattern this generalizes: ReviewDevices `deviceStatus()` (>5d ⇒
// Disconnected) and VelocityTriage `not_computed` honesty.
// ---------------------------------------------------------------------------

export type Freshness = 'fresh' | 'stale' | 'disconnected' | 'unknown';

export interface FreshnessResult {
  status: Freshness;
  ageMs: number | null;
  /** Short human label, e.g. "3 weeks ago" / "no data". */
  label: string;
}

/** Expected reporting/compute cadence per source class, in ms. */
export const CADENCE = {
  /** Hourly API poll cycle (vibration_monitor --loop). */
  hourlyPoll: 60 * 60 * 1000,
  /** MQTT live stateupdate (minutes). */
  mqttLive: 15 * 60 * 1000,
  /** Scheduled full-spectrum (reason=2) ~ every 2 days per point. */
  scheduledWaveform: 2 * 24 * 60 * 60 * 1000,
  /** Hourly derived-table cron (spectral_trends / roc_cache). */
  hourlyCompute: 60 * 60 * 1000,
  /** Daily derived-table cron (bearing_health). */
  dailyCompute: 24 * 60 * 60 * 1000,
} as const;

export interface FreshnessOpts {
  /** Expected cadence in ms (use a CADENCE preset). */
  cadenceMs: number;
  /** Multiple of cadence beyond which data is "stale". Default 3. */
  staleMult?: number;
  /** Multiple of cadence beyond which data is "disconnected". Default 12. */
  disconnectedMult?: number;
  /**
   * Timestamp format hint.
   *  - 'eastern' : "YYYY/MM/DD HH:MM:SS" wall-clock (measurements.measured_at,
   *    alarm_events.measurement_at). Parsed as local browser time.
   *  - 'utc'     : ISO-8601 with tz (devices.last_data_at, *.calculated_at).
   * Default 'eastern'. Auto-detected when the string contains 'T'/'Z'/'+'.
   */
  format?: 'eastern' | 'utc';
  /** Reference "now" in ms (for tests / synthetic staleness drills). */
  nowMs?: number;
}

/** Parse either "YYYY/MM/DD HH:MM:SS" (wall-clock) or ISO-8601 to epoch ms. */
export function parseTs(
  value: string | number | Date | null | undefined,
  format: 'eastern' | 'utc' = 'eastern',
): number | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;

  const s = value.trim();
  const looksIso = /[TZ]|[+]\d\d:?\d\d$/.test(s);
  if (format === 'utc' || looksIso) {
    const t = Date.parse(s);
    return Number.isNaN(t) ? null : t;
  }
  // Wall-clock "YYYY/MM/DD HH:MM:SS" (slashes or dashes) — parse as local time.
  const norm = s.replace(/-/g, '/');
  const m = norm.match(/^(\d{4})\/(\d{2})\/(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (m) {
    const [, y, mo, d, h, mi, se] = m;
    const t = new Date(+y, +mo - 1, +d, +h, +mi, +(se ?? '0')).getTime();
    return Number.isNaN(t) ? null : t;
  }
  const fallback = Date.parse(norm);
  return Number.isNaN(fallback) ? null : fallback;
}

function humanizeAge(ageMs: number): string {
  if (ageMs < 0) ageMs = 0;
  const mins = Math.floor(ageMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr${hrs === 1 ? '' : 's'} ago`;
  const days = Math.floor(hrs / 24);
  if (days < 14) return `${days} day${days === 1 ? '' : 's'} ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 9) return `${weeks} weeks ago`;
  const months = Math.floor(days / 30);
  return `${months} month${months === 1 ? '' : 's'} ago`;
}

/**
 * Classify a data timestamp against its source cadence.
 * Missing/unparseable timestamp ⇒ 'unknown' (neutral, never healthy-green).
 */
export function freshnessStatus(
  lastSeen: string | number | Date | null | undefined,
  opts: FreshnessOpts,
): FreshnessResult {
  const t = parseTs(lastSeen, opts.format ?? 'eastern');
  if (t == null) return { status: 'unknown', ageMs: null, label: 'no data' };

  const now = opts.nowMs ?? Date.now();
  const ageMs = now - t;
  const staleAt = opts.cadenceMs * (opts.staleMult ?? 3);
  const disconnectedAt = opts.cadenceMs * (opts.disconnectedMult ?? 12);

  let status: Freshness = 'fresh';
  if (ageMs >= disconnectedAt) status = 'disconnected';
  else if (ageMs >= staleAt) status = 'stale';
  return { status, ageMs, label: humanizeAge(ageMs) };
}

/** True only when data is fresh enough to trust a healthy (green) render. */
export function isTrustworthy(status: Freshness): boolean {
  return status === 'fresh';
}

/**
 * Resolve the color an indicator should actually paint. The health color is
 * honored ONLY when fresh; otherwise a neutral gray (stale/unknown) or a
 * darker "disconnected" gray is returned so a dead sensor can never read green.
 */
export const FRESHNESS_NEUTRAL = '#94a3b8';      // slate-400 — stale / unknown
export const FRESHNESS_DISCONNECTED = '#64748b'; // slate-500 — disconnected

export function resolveIndicatorColor(
  healthColor: string,
  status: Freshness,
): { color: string; slashed: boolean } {
  if (status === 'fresh') return { color: healthColor, slashed: false };
  if (status === 'disconnected') return { color: FRESHNESS_DISCONNECTED, slashed: true };
  return { color: FRESHNESS_NEUTRAL, slashed: false }; // stale | unknown
}

/** One-line summary suitable for a tooltip/aria-label. */
export function freshnessTitle(r: FreshnessResult): string {
  switch (r.status) {
    case 'fresh': return `Live · updated ${r.label}`;
    case 'stale': return `Stale · last data ${r.label}`;
    case 'disconnected': return `Disconnected · last data ${r.label}`;
    case 'unknown': return 'No recent-data timestamp available';
  }
}
